import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as React from "react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// RadioGroup
//
// Design: §6.6 Selection Controls
// ─────────────────────────────────────────────

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn("flex flex-col gap-2", className)}
    {...props}
  />
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      "aspect-square h-4 w-4 rounded-full border border-border-default bg-bg-input",
      "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-page",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:border-accent",
      className
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      <div className="h-2 w-2 rounded-full bg-accent" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// ── RadioGroupWithLabels ─────────────────────
interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface RadioGroupWithLabelsProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  options: RadioOption[];
  label?: string;
  errorText?: string;
}

function RadioGroupWithLabels({
  options,
  label,
  errorText,
  className,
  ...props
}: RadioGroupWithLabelsProps) {
  return (
    <div className={cn("flex flex-col gap-[6px]", className)}>
      {label && (
        <p className="text-[14px] font-medium text-text-secondary">{label}</p>
      )}
      <RadioGroup {...props}>
        {options.map((opt) => (
          <div key={opt.value} className="flex items-start gap-2.5">
            <RadioGroupItem
              value={opt.value}
              id={opt.value}
              disabled={opt.disabled}
            />
            <div className="flex flex-col gap-0.5">
              <label
                htmlFor={opt.value}
                className="cursor-pointer text-[14px] font-medium text-text-secondary"
              >
                {opt.label}
              </label>
              {opt.description && (
                <p className="text-caption text-text-muted">{opt.description}</p>
              )}
            </div>
          </div>
        ))}
      </RadioGroup>
      {errorText && (
        <p className="text-[12px] text-danger">{errorText}</p>
      )}
    </div>
  );
}

export { RadioGroup, RadioGroupItem, RadioGroupWithLabels };
